import { createAuthInterceptor, createClient } from '@lastbrand/sdk-http'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getB2BHeaders } from '@src/utils/b2b/b2b.utils'
import { getHeadersWithContentVersion, getLocalisationHeaders } from '@src/utils/general.utils'

import type { AuthInterceptorConfig, CreateClientOptions } from '@lastbrand/sdk-http'

type StorefrontClientOptions = CreateClientOptions & {
  useBearerPrefix?: AuthInterceptorConfig['useBearerPrefix']
  authInterceptor?:
    | boolean
    | {
        tokenSource?: AuthInterceptorConfig['tokenSource']
        useBearerPrefix?: AuthInterceptorConfig['useBearerPrefix']
        enabled?: boolean
      }
}

async function getDefaultAuthToken(): Promise<string | undefined> {
  const session = (await AuthUtil.getUserSession()) as { idToken?: { jwtToken?: string } } | null | undefined
  return session?.idToken?.jwtToken
}

function getStorefrontDefaultHeaders(baseURL: string): Record<string, string> {
  const contentVersionHeaders = (getHeadersWithContentVersion(undefined, baseURL) ?? {}) as Record<string, string>
  const b2bHeaders = getB2BHeaders(baseURL)
  const localisationHeaders = getLocalisationHeaders(baseURL)
  return {
    ...contentVersionHeaders,
    ...localisationHeaders,
    ...b2bHeaders,
  }
}

export function createStorefrontSdkClient(baseURL: string, options?: StorefrontClientOptions) {
  const storefrontOptions = options
  const authOption = storefrontOptions?.authInterceptor
  const authConfig = authOption != null && typeof authOption === 'object' ? authOption : undefined
  const authEnabled = authOption === true || (authConfig != null && authConfig.enabled !== false)
  const {
    authInterceptor: _authInterceptor,
    useBearerPrefix: legacyUseBearerPrefix,
    interceptors: customInterceptors,
    ...restOptions
  } = storefrontOptions ?? {}
  const useBearerPrefix = authConfig?.useBearerPrefix ?? legacyUseBearerPrefix ?? true

  const interceptors = [
    ...(authEnabled
      ? [
          createAuthInterceptor({
            tokenSource: authConfig?.tokenSource ?? getDefaultAuthToken,
            useBearerPrefix,
          }),
        ]
      : []),
    ...(customInterceptors ?? []),
  ]

  return createClient(baseURL, {
    ...restOptions,
    getDefaultHeaders: () => getStorefrontDefaultHeaders(baseURL),
    interceptors,
  })
}
